/*
 * Lightweight HTTP client for the b2c API.
 * - Always sends cookies (session is httpOnly cookie set by /auth/google/callback).
 * - Same-origin /api in production; vite dev proxies /api to staging.
 * - Throws ApiError with status + body for non-2xx so callers can branch on it.
 */

const API_BASE = (import.meta.env.VITE_API_BASE ?? '/api').replace(/\/+$/, '')

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `API error ${status}`)
    this.name = 'ApiError'
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
}

export type Body = unknown


function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${API_BASE}${path.startsWith('/') ? path : `/${path}`}`, window.location.origin)
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue
      url.searchParams.set(k, String(v))
    }
  }
  return url.toString()
}

export interface FetchExtras {
  /**
   * When true, return the raw `Response` object instead of parsing the body.
   * Caller is responsible for consuming `response.body` (e.g. SSE streaming
   * via `response.body.getReader()`). Non-2xx still throws `ApiError` with
   * a best-effort body snapshot for diagnostics.
   */
  rawResponse?: boolean
}

export async function apiFetch<T = unknown>(
  path: string,
  opts: RequestOptions = {},
  extras: FetchExtras = {},
): Promise<T> {
  const { body, query, headers, ...rest } = opts
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData
  const isStringBody = typeof body === 'string'
  const wantsJsonHeader = body !== undefined && !isFormData && !isStringBody

  const init: RequestInit = {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(wantsJsonHeader ? { 'Content-Type': 'application/json' } : {}),
      ...(headers as Record<string, string> | undefined),
    },
    ...rest,
  }
  if (body !== undefined) {
    if (isFormData) init.body = body as FormData
    else if (isStringBody) init.body = body as string
    else init.body = JSON.stringify(body)
  }

  const response = await fetch(buildUrl(path, query), init)

  if (extras.rawResponse) {
    if (!response.ok) {
      const snapshot = await response.text().catch(() => '')
      throw new ApiError(response.status, snapshot)
    }
    return response as unknown as T
  }

  const contentType = response.headers.get('content-type') ?? ''
  const isJson = contentType.includes('application/json')
  const payload = isJson ? await response.json().catch(() => null) : await response.text()

  if (!response.ok) {
    throw new ApiError(response.status, payload)
  }
  return payload as T
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: RequestOptions) =>
    apiFetch<T>(path, { ...opts, method: 'DELETE' }),
}

export function googleLoginUrl(returnTo?: string): string {
  const url = new URL(buildUrl('/auth/google/login'))
  if (returnTo) url.searchParams.set('return_to', returnTo)
  return url.toString()
}

interface ErrorEnvelope {
  detail?: unknown
  message?: string
  request_id?: string
}

function parseErrorBody(body: unknown): ErrorEnvelope | null {
  if (!body) return null
  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body) as unknown
      return parsed && typeof parsed === 'object' ? (parsed as ErrorEnvelope) : null
    } catch {
      return null
    }
  }
  if (typeof body === 'object') return body as ErrorEnvelope
  return null
}

const CENSOR_HUMAN_MESSAGE =
  'Запрос содержит недопустимый контент и не будет обработан. ' +
  'Сформулируйте задачу иначе.'

/**
 * True when the error payload is the censor block envelope
 * (either an ApiError with `{detail: {error: "censored"}}` or an SSE error
 * string containing the word "censored").
 */
export function isCensoredError(e: unknown): boolean {
  if (typeof e === 'string') return /censored/i.test(e)
  if (e instanceof ApiError) {
    const parsed = parseErrorBody(e.body)
    const detail = parsed?.detail
    if (detail && typeof detail === 'object' && (detail as { error?: unknown }).error === 'censored') {
      return true
    }
    if (typeof e.body === 'string' && /censored/i.test(e.body)) return true
  }
  return false
}

/**
 * Map an API error to text safe to show end users.
 * Handles censor blocks, common upstream statuses (402/429/502), and falls
 * back to the FastAPI `detail` string when present.
 */
export function friendlyApiError(
  e: unknown,
  fallback = 'Что-то пошло не так. Попробуйте ещё раз.',
): string {
  if (isCensoredError(e)) return CENSOR_HUMAN_MESSAGE

  if (e instanceof ApiError) {
    const parsed = parseErrorBody(e.body)
    const detail = parsed?.detail
    let detailMessage: string | undefined
    if (typeof detail === 'string') {
      detailMessage = detail
    } else if (detail && typeof detail === 'object') {
      const m = (detail as { message?: unknown }).message
      if (typeof m === 'string') detailMessage = m
    } else if (typeof parsed?.message === 'string') {
      detailMessage = parsed.message
    }

    if (e.status === 402) {
      return 'Недостаточно кредитов. Пополните баланс в разделе Биллинг.'
    }
    if (e.status === 429) {
      return 'Превышен лимит запросов. Подождите минуту и повторите.'
    }
    if (detailMessage === 'session already has an active run') {
      return 'Задача уже выполняется. Открываю текущий запуск.'
    }
    if (detailMessage === 'session expired') {
      return 'Рабочая сессия устарела. Запускаю задачу в новой сессии.'
    }
    if (detailMessage === 'failed to provision sandbox') {
      return 'Не удалось подготовить рабочую среду. Попробуйте запустить задачу ещё раз.'
    }
    if (e.status === 502 || e.status === 503 || e.status === 504) {
      return detailMessage
        ? `Сервис временно недоступен: ${detailMessage}`
        : 'Сервис временно недоступен. Попробуйте через минуту.'
    }
    if (detailMessage && detailMessage.length < 400) {
      return e.status >= 500 ? `Ошибка сервера: ${detailMessage}` : detailMessage
    }
    return `Ошибка ${e.status}`
  }

  if (e instanceof Error) return e.message
  return fallback
}

/**
 * Human-readable text for an SSE `error` event payload (`{error: "..."}`).
 * The stream channel can carry the censor verdict mid-response.
 */
export function friendlyStreamError(raw: string | null | undefined): string {
  const text = (raw || '').trim()
  if (!text) return 'Ошибка стрима. Попробуйте ещё раз.'
  if (isCensoredError(text)) return CENSOR_HUMAN_MESSAGE
  return text
}
